// @docs:
//   - docs/core/operations/OTA_CHANGELOG.md
// Admin › Changelog › Platform: what each part of the platform deployed, by
// New Zealand day. Shapes match GET /api/admin/changelog/platform.

import { useQuery } from '@tanstack/react-query';
import { api } from '../../../../services/api';
import { kindCountsOf, nzTime, parseSearch, shaMatches, type CommitAuthor, type KindCount, type RegisterRow, type SectionKind } from './changelog';

export interface PlatformChange {
  sha: string;
  url: string;
  kind: SectionKind;
  title: string;
  /** The commit's 👓 line: the change in plain English. */
  summary: string | null;
  pr: { number: number; url: string } | null;
  committedAt: string;
  author?: CommitAuthor | null;
  /** The help register rows it touches (Doc IDs, or "new <MODULE>"), from its commit's Help-guide line. */
  docs?: string[];
  /** When it went live: set by platformDays from its deploy. */
  deployedAt?: string;
}

export interface PlatformDeploy {
  part: string;
  at: string;
  sha: string;
  url: string | null;
  changes: PlatformChange[];
}

export interface PlatformLivePart {
  id: string;
  name: string;
  deployedAt: string | null;
  sha: string | null;
  latest: { state: 'failed' | 'running'; at: string | null; url: string | null } | null;
}

export interface PlatformPending {
  part: string;
  name: string;
  state: 'running' | 'failed' | 'waiting';
  url: string | null;
  changes: PlatformChange[];
}

export interface PlatformChangelogResponse {
  since: string;
  github: 'ok' | 'not_configured' | 'no_access' | 'error';
  githubError?: string;
  deploys: PlatformDeploy[];
  live: PlatformLivePart[];
  pending: PlatformPending[];
  /** The part with the most commits on main in the last 72 hours, if any. */
  busiest?: string | null;
  /** The help register rows the changes name, by Doc ID. */
  register?: Record<string, RegisterRow>;
}

export function usePlatformChangelog(enabled: boolean) {
  return useQuery<PlatformChangelogResponse>({
    queryKey: ['changelog', 'platform'],
    queryFn: () => api.adminGetPlatformChangelog(),
    staleTime: 60_000,
    enabled,
  });
}

/** One part's deploys on one New Zealand day, newest first. */
export interface DayPart {
  id: string;
  name: string;
  deploys: PlatformDeploy[];
  changes: PlatformChange[];
}

/** Everything deployed on one New Zealand day. */
export interface PlatformDay {
  /** "2026-10-19": the page address's id for it. */
  id: string;
  /** The day's first and last deploy. */
  first: string;
  last: string;
  parts: DayPart[];
}

const nzDate = (iso: string) => new Intl.DateTimeFormat('en-CA', { timeZone: 'Pacific/Auckland' }).format(new Date(iso));

/** Deploys grouped by New Zealand day, newest day first; parts in the order given. */
export function platformDays(deploys: PlatformDeploy[], order: { id: string; name: string }[]): PlatformDay[] {
  const days = new Map<string, PlatformDeploy[]>();
  for (const d of deploys) {
    const id = nzDate(d.at);
    days.set(id, [...(days.get(id) ?? []), d]);
  }
  return [...days.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([id, list]) => {
      const sorted = [...list].sort((a, b) => b.at.localeCompare(a.at));
      return {
        id,
        first: sorted[sorted.length - 1].at,
        last: sorted[0].at,
        parts: order
          .map((p) => {
            const mine = sorted.filter((d) => d.part === p.id);
            return { id: p.id, name: p.name, deploys: mine, changes: mine.flatMap((d) => d.changes.map((c) => ({ ...c, deployedAt: d.at }))) };
          })
          .filter((p) => p.deploys.length),
      };
    });
}

export const changeKindCounts = (changes: PlatformChange[]): KindCount[] => kindCountsOf(changes.map((c) => c.kind));

/** "Deployed 4:10 pm", or "Deployed 3 times · 9:20 am to 4:10 pm". */
export function deployedLine(deploys: { at: string }[]): string {
  if (deploys.length === 1) return `Deployed ${nzTime(deploys[0].at)}`;
  const times = deploys.map((d) => d.at).sort();
  return `Deployed ${deploys.length} times · ${nzTime(times[0])} to ${nzTime(times[times.length - 1])}`;
}

// ---- copy formats ----

function changeLine(c: PlatformChange, md: boolean): string {
  const refs = md ? [c.pr && `[#${c.pr.number}](${c.pr.url})`, `[${c.sha}](${c.url})`] : [c.pr && `#${c.pr.number}`, c.sha];
  const head = `- ${c.title} (${refs.filter(Boolean).join(', ')})`;
  return c.summary ? `${head}\n  ${c.summary}` : head;
}

export function partText(p: DayPart, md = false): string {
  const title = p.deploys.length ? `${p.name}: ${deployedLine(p.deploys).replace(/^Deployed/, 'deployed')}` : `${p.name}: not live yet`;
  return [md ? `### ${title}` : title, '', ...p.changes.map((c) => changeLine(c, md))].join('\n');
}

export function dayText(day: PlatformDay, heading: string, md = false): string {
  return [md ? `## ${heading}` : heading, ...day.parts.map((p) => `\n${partText(p, md)}`)].join('\n');
}

// ---- search ----

export interface PlatformHit {
  /** A day id, or "pending". */
  where: string;
  part: string;
  name: string;
  at: string | null;
  change: PlatformChange;
  /** What matched: its words, its commit id or its PR. */
  by: 'text' | 'sha' | 'pr';
}

/** Every change matching `q` (its title, its plain-English line, its commit id or its PR), not-live-yet first. */
export function searchPlatform(days: PlatformDay[], pending: PlatformPending[], q: string): PlatformHit[] {
  const query = parseSearch(q);
  const match = (c: PlatformChange): PlatformHit['by'] | null => {
    if (shaMatches(query, c.sha, c.url)) return 'sha';
    if (query.pr !== null && c.pr?.number === query.pr) return 'pr';
    if (c.title.toLowerCase().includes(query.text) || c.summary?.toLowerCase().includes(query.text)) return 'text';
    return null;
  };
  const hits = (where: string, part: string, name: string, changes: PlatformChange[]): PlatformHit[] =>
    changes.flatMap((c) => {
      const by = match(c);
      return by ? [{ where, part, name, at: c.deployedAt ?? null, change: c, by }] : [];
    });
  return [
    ...pending.flatMap((p) => hits('pending', p.part, p.name, p.changes)),
    ...days.flatMap((d) => d.parts.flatMap((p) => hits(d.id, p.id, p.name, p.changes))),
  ];
}
